import type { SafelyFetchGraphQlFn } from "@/domains/api/safely-fetch-graphql"

import type { EpochApiData } from "./types"

export class EpochGraphQlService implements IEpochGraphQlService {
  private readonly baseUrl: string
  private readonly safelyFetchGraphQl: SafelyFetchGraphQlFn

  constructor({ baseUrl, safelyFetchGraphQl }: { baseUrl: string; safelyFetchGraphQl: SafelyFetchGraphQlFn }) {
    this.baseUrl = baseUrl
    this.safelyFetchGraphQl = safelyFetchGraphQl
  }

  /**
   * Fetch the latest epoch from GraphQL
   * Query: Epoch(order_by: {epochNumber: desc}, limit: 1)
   */
  async getCurrentEpoch(): Promise<EpochApiData | null> {
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query GetCurrentEpoch {
          Epoch(order_by: {epochNumber: desc}, limit: 1) {
            epochNumber
            startTimestamp
            dists
            weekly
            mints
            gaugesMint
            mintBlockNumber
            recycled
            pool2Mint
            teamMint
            recycledPercentage
            freshPercentage
            circulatingEmission
            circulatingSupply
          }
        }`,
        variables: null,
        operationName: "GetCurrentEpoch",
      }),
      returnOnError: null,
    })

    if (!response || typeof response !== "object" || !("Epoch" in response)) {
      return null
    }

    const epochs = (response as { Epoch: unknown[] }).Epoch
    if (!Array.isArray(epochs) || epochs.length === 0) {
      return null
    }

    return epochs[0] as EpochApiData
  }

  /**
   * Fetch epochs from GraphQL with minimum epoch number filter
   * Query: Epoch(where: {epochNumber: {_gte: minEpochNumber}}, order_by: {epochNumber: desc})
   */
  async getEpochs(minEpochNumber: number): Promise<EpochApiData[]> {
    const response = await this.safelyFetchGraphQl({
      url: this.baseUrl,
      body: JSON.stringify({
        query: `query GetEpochs($minEpochNumber: Int!) {
          Epoch(where: {epochNumber: {_gte: $minEpochNumber}}, order_by: {epochNumber: desc}) {
            epochNumber
            startTimestamp
            dists
            weekly
            mints
            gaugesMint
            mintBlockNumber
            recycled
            pool2Mint
            teamMint
            recycledPercentage
            freshPercentage
            circulatingEmission
            circulatingSupply
          }
        }`,
        variables: { minEpochNumber },
        operationName: "GetEpochs",
      }),
      returnOnError: [],
    })

    if (!response || typeof response !== "object" || !("Epoch" in response)) {
      return []
    }

    const epochs = (response as { Epoch: unknown[] }).Epoch
    if (!Array.isArray(epochs)) {
      return []
    }

    return epochs as EpochApiData[]
  }
}

// Import interface after class definition to avoid circular dependency
import type { IEpochGraphQlService } from "./interfaces"
